// client/src/components/profissionais/sections/ServicesSection.js
import React, { useState, useCallback, useEffect, useRef } from 'react';
import UserServicesTab from '../tabs/UserServicesTab';
import { useRelationshipsMutations, useUserUnidadesByUser, useUserServicosByUser } from '../../../hooks/useRelationships';
import { Save, Loader2, Settings, XCircle, CheckCircle, AlertCircle } from 'lucide-react';
import useAlert from '../../../hooks/useAlert';

// Função segura para extrair IDs
const extractId = (obj) => {
    if (!obj) return null;
    if (typeof obj === 'string') return obj;
    if (obj._id) return String(obj._id);
    if (obj.id) return String(obj.id);
    return String(obj);
};

const ServicesSection = ({ user, contextData }) => {
    const { setAlert } = useAlert();
    
    const { 
        createUserServico, 
        updateUserServico, 
        deleteUserServico,
        invalidateRelationships 
    } = useRelationshipsMutations();
    
    // Busca os serviços que o profissional já faz
    const { 
        data: servicosVinculadosData, 
        isLoading: isLoadingVinculos,
        refetch: refetchVinculos 
    } = useUserServicosByUser(
        user?._id, 
        contextData?.contaId,
        { enabled: !!user?._id }
    );

    // Busca as unidades onde o profissional trabalha
    const { data: userUnidadesData } = useUserUnidadesByUser(
        user?._id, 
        contextData?.contaId,
        { enabled: !!user?._id }
    );
    
    const [selectedServicos, setSelectedServicos] = useState([]);
    const [comissoesConfig, setComissoesConfig] = useState({});
    const [unidadesConfig, setUnidadesConfig] = useState({}); 
    const [existingVinculos, setExistingVinculos] = useState([]);
    
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    
    // Ref para garantir a carga inicial sem loop
    const didLoadRef = useRef(false);

    // Sincronização Segura
    const carregarDadosDoBanco = useCallback(() => {
        if (!servicosVinculadosData?.data) return;
        
        const vinculos = servicosVinculadosData.data.filter(v => v.status === 'ativo');
        setExistingVinculos(vinculos);
        
        const servicosIds = [];
        const comissoes = {};
        const unidades = {}; 
        
        vinculos.forEach(vinculo => {
            const servicoId = extractId(vinculo.servico);
            
            if (servicoId) {
                servicosIds.push(servicoId);
                comissoes[servicoId] = vinculo.comissao || {};
                
                if (Array.isArray(vinculo.unidades)) {
                    unidades[servicoId] = vinculo.unidades.map(u => extractId(u)).filter(Boolean);
                } else {
                    unidades[servicoId] = [];
                }
            }
        });
        
        setSelectedServicos(servicosIds);
        setComissoesConfig(comissoes);
        setUnidadesConfig(unidades);
        didLoadRef.current = true;
    }, [servicosVinculadosData]);

    useEffect(() => {
        carregarDadosDoBanco();
    }, [carregarDadosDoBanco]);

    const handleServicesChange = useCallback((selectedIds, comissoes, unidadesPorServico) => {
        setSelectedServicos(selectedIds);
        if (comissoes) setComissoesConfig(comissoes);
        if (unidadesPorServico) setUnidadesConfig(unidadesPorServico);
    }, []);

    const handleSave = async () => {
        if (!user?._id || !contextData?.contaId) {
            setAlert('Erro de identificação do usuário ou conta', 'error');
            return;
        }

        setIsSaving(true);

        try {
            const servicosParaProcessar = [...selectedServicos];
            const resultados = [];
            const errors = [];

            // Pega as unidades gerais do profissional como fallback
            const unidadesDoProfissional = userUnidadesData?.data?.filter(vu => vu.status === 'ativo').map(vu => extractId(vu.unidade)).filter(Boolean) || [];

            for (const servicoId of servicosParaProcessar) {
                try {
                    const vinculoExistente = existingVinculos.find(v => {
                        const vinculoServicoId = extractId(v.servico);
                        return vinculoServicoId === servicoId && String(v.account?._id || v.account) === String(contextData.contaId);
                    });

                    const comissao = comissoesConfig[servicoId] || {};
                    let unidadesDoServico = unidadesConfig[servicoId] || []; 
                    
                    // Se não tiver unidades marcadas ao salvar, preenche com as unidades que o profissional trabalha
                    if (unidadesDoServico.length === 0) {
                        unidadesDoServico = unidadesDoProfissional;
                    }
                    
                    const payload = {
                        user: user._id,
                        servico: servicoId,
                        account: contextData.contaId,
                        unidades: unidadesDoServico,
                        status: 'ativo',
                        comissao: comissao
                    };

                    if (vinculoExistente) {
                        await updateUserServico.mutateAsync({
                            id: vinculoExistente._id,
                            data: { 
                                comissao,
                                unidades: unidadesDoServico,
                                status: 'ativo'
                            }
                        });
                        resultados.push({ servicoId, action: 'updated' });
                    } else {
                        await createUserServico.mutateAsync(payload);
                        resultados.push({ servicoId, action: 'created' });
                    }
                } catch (error) {
                    console.error(`❌ Erro no serviço ${servicoId}:`, error);
                    errors.push({ servicoId, error: error.message });
                }
            }

            // Remover serviços desmarcados no UI
            const servicosParaRemover = existingVinculos
                .filter(v => String(v.account?._id || v.account) === String(contextData.contaId))
                .filter(v => {
                    const vinculoServicoId = extractId(v.servico);
                    return !selectedServicos.includes(vinculoServicoId);
                });

            for (const vinculo of servicosParaRemover) {
                try {
                    await deleteUserServico.mutateAsync(vinculo._id);
                    resultados.push({ servicoId: vinculo.servico, action: 'deleted' });
                } catch (error) {
                    console.error(`❌ Erro ao remover vínculo:`, error);
                }
            }

            invalidateRelationships();
            
            if (errors.length === 0) {
                setAlert('Serviços e unidades atualizados com sucesso!', 'success');
            } else {
                setAlert(`Atualização concluída com erros.`, 'warning');
            }

            await refetchVinculos();
            carregarDadosDoBanco(); 
            setIsEditing(false);

        } catch (error) {
            console.error('❌ [ServicesSection] Erro geral:', error);
            setAlert('Erro ao salvar serviços.', 'danger');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        carregarDadosDoBanco(); 
        setIsEditing(false);
    };

    const isLoading = isLoadingVinculos || isSaving || !didLoadRef.current;
    const isPending = createUserServico.isPending || updateUserServico.isPending || deleteUserServico.isPending;
    const isProfissional = !!user?.profissionalId || user?.isProfissional; 

    const unidadesCompletasParaCheckboxes = userUnidadesData?.data?.filter(v => v.status === 'ativo').map(v => v.unidade).filter(Boolean) || [];

    if (isLoading && !didLoadRef.current) {
        return (
            <div className="flex justify-center items-center py-12">
                <Loader2 size={32} className="animate-spin text-blue-600" />
            </div>
        );
    }

    if (!isProfissional) {
        return (
            <div className="form-section text-center py-12 border border-dashed border-gray-200 mt-4 rounded-xl bg-gray-50">
                <Settings size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Apenas Profissionais</h3>
                <p className="text-gray-500 mb-4">Habilite a opção "É Profissional" na aba Perfil.</p>
            </div>
        );
    }

    return (
        <div className="form-content">
            <div className="form-header flex items-start justify-between mb-2">
                <div className="flex-1">
                    <h3 className="form-title flex items-center mb-1 text-primary text-lg">
                        <Settings size={22} className="mr-2"/>
                        Serviços Habilitados
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">Gerencie os serviços que este profissional realiza</p>
                    
                    {!isEditing && existingVinculos.length > 0 && (
                        <div className="text-xs text-green-600 mt-2 flex items-center font-medium bg-green-50 w-fit px-2 py-1 rounded-md">
                            <CheckCircle size={14} className="mr-1" />
                            {existingVinculos.length} vínculo(s) ativo(s)
                        </div>
                    )}

                    {!isEditing && existingVinculos.length === 0 && (
                        <div className="text-xs text-gray-500 mt-2 flex items-center bg-gray-100 w-fit px-2 py-1 rounded-md">
                            <AlertCircle size={14} className="mr-1" />
                            Nenhum serviço vinculado
                        </div>
                    )}
                </div>
                
                {!isEditing ? (
                    <button 
                        onClick={() => setIsEditing(true)} 
                        className="action-button action-button-secondary bg-white border border-gray-300 shadow-sm text-gray-700"
                        disabled={isLoading}
                    >
                        <Settings size={16} className="mr-2 text-gray-500" />
                        Editar Serviços
                    </button>
                ) : (
                    <div className="flex gap-2">
                        <button 
                            onClick={handleCancel} 
                            className="action-button action-button-secondary bg-white border border-gray-300 shadow-sm text-gray-700"
                        >
                            <XCircle size={16} className="mr-2 text-gray-500" /> Cancelar
                        </button>
                        <button 
                            onClick={handleSave} 
                            disabled={isLoading || isPending} 
                            className="action-button action-button-primary bg-primary text-white shadow-sm"
                        >
                            {isLoading || isPending ? (
                                <>
                                    <Loader2 size={16} className="animate-spin mr-2" />
                                    Salvando...
                                </>
                            ) : (
                                <>
                                    <Save size={16} className="mr-2" />
                                    Salvar Serviços
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>

            <div className="form-tabs-content p-0 mt-4 border-t border-gray-100 pt-6">
                <UserServicesTab 
                    userId={user._id}
                    contaId={contextData?.contaId}
                    selectedServicos={selectedServicos}
                    comissoesConfig={comissoesConfig}
                    unidadesConfig={unidadesConfig} 
                    unidadesDisponiveis={unidadesCompletasParaCheckboxes} 
                    onServicosChange={handleServicesChange}
                    isProfissional={isProfissional}
                    isEditing={isEditing}
                    userNome={user?.name}
                    refetchVinculos={refetchVinculos}
                />
            </div>
        </div>
    );
};

export default ServicesSection;